/**
 * Persistent "Reclaim unused slots" card on Settings → Workspace.
 *
 * Surfaces paid team-member and bank-account add-on slots the tenant is no
 * longer using (e.g. after deactivating a team member or deleting a bank
 * account). Tapping Reclaim drops ONE slot per click so admins stay in
 * control — a firm with 3 empty seats will see "Reclaim 1 more" after the
 * first click until the counter hits zero and the card hides itself.
 *
 * Note: see /app/backend/routes_billing.py — recurring Stripe subscription
 * decrement is still TODO. For now this only lowers the local cap. Stripe
 * cutover will plug in later.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ReceiptText, Users as UsersIcon, Landmark, Loader2, RotateCcw } from "lucide-react";

export default function ReclaimSlotsCard() {
  const { refreshMe } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null); // "team" | "bank" | null

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/billing/unused-slots");
      setData(data);
    } catch {
      setData(null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const reclaim = async (kind) => {
    setBusy(kind);
    try {
      const path = kind === "team" ? "/billing/team-members/reclaim" : "/billing/bank-accounts/reclaim";
      const { data: resp } = await api.post(path);
      const dollars = Number(resp?.monthly_savings || 0);
      toast.success(
        dollars > 0
          ? `Reclaimed 1 slot — saving $${dollars.toFixed(2)}/mo`
          : "Reclaimed 1 slot",
      );
      await Promise.all([load(), refreshMe?.()]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reclaim slot");
    } finally {
      setBusy(null);
    }
  };

  const teamUnused = Number(data?.team_members?.unused || 0);
  const bankUnused = Number(data?.bank_accounts?.unused || 0);
  const teamSave = Number(data?.team_members?.monthly_savings_per_slot || 0);
  const bankSave = Number(data?.bank_accounts?.monthly_savings_per_slot || 0);

  // Hide completely when nothing to reclaim — zero-noise principle.
  if (!data || (teamUnused <= 0 && bankUnused <= 0)) return null;

  const totalSavings = teamUnused * teamSave + bankUnused * bankSave;

  return (
    <div
      className="ll-card-flat p-5 mb-6"
      style={{ background: "#FFFBEB", borderColor: "#FCD34D" }}
      data-testid="reclaim-slots-card"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)] flex items-center gap-1.5">
            <ReceiptText size={11} /> Reclaim unused slots
          </div>
          <div className="font-display font-bold text-xl mt-1">
            ${totalSavings.toFixed(2)}
            <span className="text-[var(--ll-ink-soft)] text-base font-normal">/mo could be saved</span>
          </div>
          <p className="text-xs text-[var(--ll-ink-soft)] mt-1 max-w-prose">
            You&apos;re paying for add-on slots nothing is using. Drop them one at a time — you can always buy them back later.
          </p>
        </div>
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        {teamUnused > 0 && (
          <div
            className="flex items-center justify-between gap-3 p-3 bg-white border"
            style={{ borderColor: "#FDE68A" }}
            data-testid="reclaim-team-member-row"
          >
            <div className="flex items-center gap-2 min-w-0">
              <UsersIcon size={14} className="shrink-0 opacity-70" />
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">
                  {teamUnused} empty team-member seat{teamUnused === 1 ? "" : "s"}
                </div>
                <div className="text-[11px] text-[var(--ll-ink-soft)]">
                  ${teamSave.toFixed(2)}/mo per slot
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => reclaim("team")}
              disabled={busy === "team"}
              data-testid="reclaim-team-member-btn"
              className="text-xs px-3 py-2 bg-[var(--ll-ink)] text-white hover:bg-[#2D6A4F] flex items-center gap-1.5 disabled:opacity-60 shrink-0"
            >
              {busy === "team" ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />}
              Drop 1
            </button>
          </div>
        )}

        {bankUnused > 0 && (
          <div
            className="flex items-center justify-between gap-3 p-3 bg-white border"
            style={{ borderColor: "#FDE68A" }}
            data-testid="reclaim-bank-account-row"
          >
            <div className="flex items-center gap-2 min-w-0">
              <Landmark size={14} className="shrink-0 opacity-70" />
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">
                  {bankUnused} empty bank-account slot{bankUnused === 1 ? "" : "s"}
                </div>
                <div className="text-[11px] text-[var(--ll-ink-soft)]">
                  ${bankSave.toFixed(2)}/mo per slot
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => reclaim("bank")}
              disabled={busy === "bank"}
              data-testid="reclaim-bank-account-btn"
              className="text-xs px-3 py-2 bg-[var(--ll-ink)] text-white hover:bg-[#2D6A4F] flex items-center gap-1.5 disabled:opacity-60 shrink-0"
            >
              {busy === "bank" ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />}
              Drop 1
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
